// Considered: ComposerGalleryOrganism — rejected. It is hard-typed to `ComposerGalleryEntry`/
// `targetKind`, carries Space-specific "New…" actions and a live-preview slot; generalizing it
// to also cover org-private templates would touch the shipped Space gallery for no shared
// behaviour beyond layout. This organism mirrors its contract shape (entries/sections, loading,
// error, filter + search controlled props, onOpen) without the coupling. See spec Design System
// Reuse Audit.
import React from 'react';
import { useTranslation } from 'localization';
import type { StudioTemplateCard } from 'types/studio';
import InputAtom from '../../atoms/Input/InputAtom';
import { SegmentedControlAtom } from '../../atoms/SegmentedControl';
import EmptyStateAtom from '../../atoms/EmptyState/EmptyStateAtom';
import SkeletonAtom from '../../atoms/Skeleton/SkeletonAtom';
import StudioTemplateCardMolecule from '../../molecules/studio/StudioTemplateCardMolecule';
import './StudioTemplateGalleryOrganism.scss';

export type StudioGalleryStatusFilter = 'all' | 'draft' | 'published';

export interface StudioGallerySection {
  /** Unique section key (typically the template type it groups, e.g. `approach`, `schema`). */
  key: string;
  /** i18n key for the section header, e.g. `studio.gallery.section.approach`. */
  labelKey: string;
  cards: StudioTemplateCard[];
}

export interface StudioTemplateGalleryOrganismProps {
  sections: StudioGallerySection[];
  loading?: boolean;
  errorMessage?: string;
  statusFilter: StudioGalleryStatusFilter;
  onStatusFilterChange: (filter: StudioGalleryStatusFilter) => void;
  /**
   * Whether the Published/Drafts control renders. Callers pass false for builders whose
   * `templateTypes` cannot report lifecycle (approach/schema report `status: null` forever —
   * EAINT-873 review, owner decision 2026-09-01): a filter whose non-`all` values can never
   * match anything is worse than no filter. Default true.
   */
  showStatusFilter?: boolean;
  searchTerm: string;
  onSearchTermChange: (term: string) => void;
  onOpen: (card: StudioTemplateCard) => void;
  /**
   * Batched distribution-lookup results, keyed `${type}::${systemName}` -> channel count.
   * `null` = the lookup hasn't resolved yet or failed — every card renders with no distribution
   * chip (never a fabricated "Private"). Missing key within a non-null map = 0 channels
   * (indistinguishable from "checked, unlisted").
   */
  distribution: Record<string, number> | null;
  /** True only when the batch lookup rejected — renders a quiet degraded notice above the grid. */
  distributionDegraded?: boolean;
  emptyTitle: string;
  emptyDescription?: string;
}

const STATUS_FILTERS: StudioGalleryStatusFilter[] = ['all', 'draft', 'published'];

/**
 * The gallery body for a `browse`-status Studio builder (EAINT-873): search + status-filter
 * toolbar, sectioned card grid, and honest empty/loading/error states. Filtering by search term
 * happens in the caller (the page already narrows `sections` before handing them down, mirroring
 * `ComposerGalleryOrganism`'s contract); this organism only applies the controlled `statusFilter`
 * to the sections it's handed, and never widens what it's given.
 */
const StudioTemplateGalleryOrganism: React.FC<StudioTemplateGalleryOrganismProps> = ({
  sections,
  loading = false,
  errorMessage,
  statusFilter,
  onStatusFilterChange,
  showStatusFilter = true,
  searchTerm,
  onSearchTermChange,
  onOpen,
  distribution,
  distributionDegraded = false,
  emptyTitle,
  emptyDescription,
}) => {
  const { t } = useTranslation();

  const filteredSections = sections
    .map((section) => ({
      ...section,
      cards: section.cards.filter((card) => statusFilter === 'all' || card.status === statusFilter),
    }))
    .filter((section) => section.cards.length > 0);

  /**
   * Loading / error / empty / sections, in that precedence. Kept as a statement rather than a
   * chain of nested ternaries in the JSX (Sonar S3358) — the error branch renders nothing here
   * because the alert above already carries the message.
   */
  const renderContent = () => {
    if (loading) {
      return (
        <div className="studio-template-gallery__skeleton" data-testid="studio-template-gallery-skeleton">
          <SkeletonAtom shape="rectangle" />
          <SkeletonAtom shape="rectangle" />
          <SkeletonAtom shape="rectangle" />
        </div>
      );
    }
    if (errorMessage) return null;
    if (filteredSections.length === 0) {
      return <EmptyStateAtom icon="pi-th-large" title={emptyTitle} description={emptyDescription} />;
    }
    return (
      <div className="studio-template-gallery__sections">
        {filteredSections.map((section) => (
          <div key={section.key} className="studio-template-gallery__section" data-testid={`studio-gallery-section-${section.key}`}>
            <h3 className="studio-template-gallery__section-header">{t(section.labelKey)}</h3>
            <div className="studio-template-gallery__grid">
              {section.cards.map((card) => {
                const distributionKey = `${card.type}::${card.systemName}`;
                return (
                  <StudioTemplateCardMolecule
                    key={distributionKey}
                    card={card}
                    distributionChannelCount={distribution ? (distribution[distributionKey] ?? 0) : null}
                    onOpen={onOpen}
                  />
                );
              })}
            </div>
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="studio-template-gallery" data-testid="studio-template-gallery">
      <div className="studio-template-gallery__toolbar">
        <InputAtom
          value={searchTerm}
          placeholder={t('studio.gallery.searchPlaceholder')}
          onChange={(e) => onSearchTermChange(e.target.value)}
          aria-label={t('studio.gallery.searchPlaceholder')}
        />
        {showStatusFilter && (
          <SegmentedControlAtom
            options={STATUS_FILTERS.map((filter) => ({ value: filter, label: t(`studio.gallery.statusFilter.${filter}`) }))}
            value={statusFilter}
            onChange={(value) => onStatusFilterChange(value as StudioGalleryStatusFilter)}
            mode="single"
            variant="pills"
            size="small"
            ariaLabel={t('studio.gallery.statusFilterLabel')}
          />
        )}
      </div>

      {errorMessage && (
        <p className="studio-template-gallery__error" role="alert">
          {errorMessage}
        </p>
      )}

      {distributionDegraded && (
        <output className="studio-template-gallery__degraded-notice">
          {t('studio.gallery.distributionDegraded')}
        </output>
      )}

      {renderContent()}
    </div>
  );
};

export default StudioTemplateGalleryOrganism;
